<template>
  <v-dialog v-model="show" width="1100" scrollable>
    <v-card>
      <v-toolbar>
        <v-toolbar-title>Audit Log — {{ distributorLabel }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" @click="close" aria-label="Close audit log"></v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"></v-progress-linear>

        <v-data-table
          v-if="!loading"
          :headers="headers"
          :items="auditItems"
          :items-per-page="25"
          :items-per-page-options="[10, 25, 50, 100]"
          density="compact"
          role="table"
          aria-label="Distributor audit log">

          <template v-slot:item.operation="{ item }">
            <v-chip
              :color="operationColor(item.operation)"
              size="small"
              label>
              {{ item.operation }}
            </v-chip>
          </template>

          <template v-slot:item.oldValue="{ item }">
            <span class="text-error text-caption">{{ item.oldValue ?? '—' }}</span>
          </template>

          <template v-slot:item.newValue="{ item }">
            <span class="text-success text-caption">{{ item.newValue ?? '—' }}</span>
          </template>

          <template v-slot:item.modifiedAt="{ item }">
            {{ formatDate(item.modifiedAt) }}
          </template>

          <template v-slot:no-data>
            <span class="text-medium-emphasis">No audit records found for this distributor.</span>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions class="border-t-thin">
        <v-btn @click="close" width="100">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useDistributorStore } from '@/stores/distributor'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  distributorId: {
    type: Number,
    default: null
  },
  distributorLabel: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue'])

const distributorStore = useDistributorStore()

const show = ref(props.modelValue)
const loading = ref(false)
const auditItems = ref([])

const headers = [
  { key: 'auditId',     title: 'ID',         sortable: true,  width: '70px' },
  { key: 'operation',   title: 'Operation',  sortable: true,  width: '110px' },
  { key: 'columnName',  title: 'Column',     sortable: true },
  { key: 'oldValue',    title: 'Old Value',  sortable: false },
  { key: 'newValue',    title: 'New Value',  sortable: false },
  { key: 'modifiedBy',  title: 'Modified By', sortable: true },
  { key: 'modifiedAt',  title: 'Modified At', sortable: true }
]

watch(() => props.modelValue, (val) => {
  show.value = val
})

watch(show, (val) => {
  emit('update:modelValue', val)
  if (val) {
    loadAuditLog()
  }
})

const loadAuditLog = () => {
  if (!props.distributorId) return
  loading.value = true
  auditItems.value = []
  distributorStore.getDistributorAuditLog(props.distributorId).then(result => {
    if (result.success && result.hasData) {
      auditItems.value = result.auditLog
    }
  }).catch(error => {
    console.log('getDistributorAuditLog', error)
  }).finally(() => {
    loading.value = false
  })
}

const close = () => {
  show.value = false
}

const operationColor = (op) => {
  switch (op?.toUpperCase()) {
    case 'INSERT': return 'success'
    case 'UPDATE': return 'warning'
    case 'DELETE': return 'error'
    default:       return 'default'
  }
}

const formatDate = (value) => {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}
</script>
